import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormikProps } from 'formik';
import SingleOrderField from './SingleOrderField';
import SectionHeader from '../../../components/SectionHeader';
import { ISingleOrderEntity, ISingleOrderField, IStepData } from '../types/interfaces';
import StepCommentsSidebar from './StepCommentsSidebar';
import gsap from 'gsap'

interface IProps {
  data: ISingleOrderEntity;
  formik: FormikProps<any>;
  index: number;
  stepData: IStepData;
  canUpdateStepValues: boolean
  canApproveStep: boolean
}

const SingleOrderEntity = ({ data, formik, index, stepData, canApproveStep, canUpdateStepValues }: IProps) => {
  const { i18n } = useTranslation();
  const [currOpenField, setCurrOpenField] = useState<ISingleOrderField | null>(null);
  const animationRef = useRef<gsap.core.Timeline>();
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    formik.setFieldValue(`entities.entity_${data?.id}.entity_id`, data?.id);
  }, [data, index]);

  function closeCommentsSide() {
    if (animationRef?.current) animationRef?.current?.kill();
    const tl: gsap.core.Timeline = gsap.timeline();
    animationRef.current = tl;
    const sidebarOverlay: HTMLDivElement = sidebarRef?.current?.closest('.comments-overlay') as HTMLDivElement;
    tl.to(sidebarRef?.current, {
      x: 1000,
      duration: 0.5, ease: "power3.inOut"
    })
      .to(
        sidebarOverlay,
        {
          autoAlpha: 0,
          duration: 0.5, ease: "power3.inOut"
        },
        '<'
      )
      .call(() => {
        setCurrOpenField(null);
      });
  }

  useEffect(() => {
    if(currOpenField){
      setCurrOpenField(stepData?.entities?.flatMap((entity: any) => entity?.fields)?.find((field: any) => field?.id === currOpenField?.id))
    }
  }, [stepData])

  const {canAddComment, canUpdateEntityValues} = useMemo(() => {
    const canUpdateEntityValues = canUpdateStepValues && !data?.editable_by_customer && !data?.auto_generate_documents

    // const canAddComment = canApproveStep && !stepData?.customer_responsible && !data?.auto_generate_documents && data?.editable_by_customer

    return {
      canUpdateEntityValues,
      canAddComment: false
    }
  }, [data, stepData, canApproveStep, canUpdateStepValues])

  return (
    <>
      <StepCommentsSidebar
        sideRef={sidebarRef}
        field={currOpenField}
        closeCommentsSide={closeCommentsSide}
        canAddComment={canAddComment}
      />
      <SectionHeader title={typeof data?.name == 'string' ? data?.name : data?.name[i18n.language as 'en' | 'ar']} customStyle="last">
        <div className="field-array-container">
          <div className="fields_container">
            {data?.fields?.map((item, fieldIndex: number) => (
              <SingleOrderField
                data={item}
                key={fieldIndex}
                entityId={Number(data?.id)}
                formik={formik}
                disabled={!canUpdateEntityValues}
                setCurrOpenField={setCurrOpenField}
                currOpenField={currOpenField}
                closeCommentsSide={closeCommentsSide}
                animationRef={animationRef}
                sidebarRef={sidebarRef}
                canApproveStep={canApproveStep && canAddComment}
                stepData={stepData}
              />
            ))}
          </div>
        </div>
      </SectionHeader>
    </>
  );
};

export default SingleOrderEntity;
